<template>
  <base-card
    class="overflow-hidden d-flex pl-4"
    medium
  >
    <div class="d-flex justify-content-between align-items-center">
      <base-heading class="drafts-title mb-0">
        Appointments
      </base-heading>
      <div class="d-flex">
        <base-search
          v-model="search"
          class="search-drafts"
        />
        <base-button
          class="px-3 mr-0 d-flex"
          nowrap
          small
          inverse
          @click="onNewAppointment"
        >
          <base-icon
            icon="white-plus"
            class="mr-2"
          />
          <base-text
            light
            size="s"
          >
            New Appointment
          </base-text>
        </base-button>
      </div>
    </div>
    <hr class="div">
    <div class="row">
      <div class="col-2 pl-0">
        <div
          v-for="(filter, index) in filters"
          :key="`filter-${index}`"
          :class="`filteritem ${index === filterIndex ? 'selected' : ''}`"
          @click="filterIndex = index"
        >
          {{ filter.name }}
        </div>
      </div>
      <div class="col-10">
        <appointments-notification :upcoming="upcoming" />
        <div class="d-flex flex-column">
          <div
            v-if="loading"
          >
            <base-card
              v-for="i in 3"
              :key="i"
              class="mt-3 mb-2 skeleton position-relative"
              condensed
            >
              <div class="d-flex w-100">
                <div class="thumb mr-2" />
                <base-skeleton
                  no-background
                  class="w-100"
                >
                  <!--<div
                    class="skeleton__item"
                    style="width: 100%; height: 480px;"
                  />-->
                  <div class="skeleton__item w-75 ml-4" />
                  <div class="skeleton__item w-75 ml-4 mb-5" />
                  <div class="skeleton__item w-75 ml-4" />
                  <div class="skeleton__item w-75 ml-4" />
                  <div class="skeleton__item w-75 ml-4" />
                </base-skeleton>
              </div>
            </base-card>
          </div>
          <div v-else>
            <base-list
              :page-size="pageSize"
              :items="elements"
              :count="count"
              :mode="mode"
              :page="truePage"
              @reload="onReload"
              @pageChange="onPageChange"
            >
              <template #default="{item}">
                <appointments-cell
                  :button-join="currentFilter.name !== 'Past'"
                  :past="currentFilter.queryparams.past"
                  :item="item"
                  @view="onViewAppointment"
                  @delete="preDeleteProcess(item)"
                />
              </template>
              <template #nodata>
                <div class="no-appointments">
                  <template v-if="currentFilter.name === 'Past'">
                    <empty title="No past appointments yet">
                      Completed appointments will appear here.<br>
                      <a
                        is="router-link"
                        href="#"
                        class="link-orange font-weight-bold"
                        to="/appointments/create"
                      >Create a new one</a> to get started!
                    </empty>
                  </template>
                  <template v-else>
                    <empty title="No upcoming appointments">
                      <a
                        is="router-link"
                        href="#"
                        class="link-orange font-weight-bold"
                        to="/appointments/create"
                      >Create a new one</a> to schedule one.
                    </empty>
                  </template>
                </div>
              </template>
            </base-list>
          </div>
        </div>
      </div>
    </div>
  </base-card>
</template>

<script>

import { appointmentsService } from '@/_services';
import Listable from '@/components/mixins/listable';
import AppointmentsNotification from '@/components/Widgets/Appointments/AppointmentsNotification';
import { EventBus } from '@/event-bus';
import { APPOINTMENT_TYPES } from '@/_utilities/Enums';
import AppointmentsCell from './AppointmentsCell';

export default {

  name: 'AppointmentsListContainer',
  components: { AppointmentsNotification, AppointmentsCell },
  mixins: [Listable],
  props: {
    source: {
      type: String,
      default: 'appointments',
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    mode: {
      type: String,
      default: 'pagination',
    },
  },
  data() {
    return {
      filterIndex: 0,
      upcoming: [],
      showDeleteDialog: false,
      deletingItem: null,
      deleteMessage: '',
    };
  },
  computed: {
    currentFilter() {
      return this.filters[this.filterIndex];
    },
    filters() {
      return [{
        name: 'Upcoming',
        queryparams: {},
      }, {
        name: 'Past',
        queryparams: { past: true },
      }];
    },
    loading() { return this.$store.state.appointments.isLoading; },
    count() {
      return this.$store.state.appointments.count;
    },
  },
  watch: {
    search(val) {
      if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
        this.$mixpanel.track('search_album', {
          account_id: this.$store.state.account.user.user.email,
          appointments_search: val,
        });
      }
    },
    currentFilter(newval) {
      this.truePage = 1;
      this.queryParams = newval.queryparams;
    },
  },
  mounted() {
    this.fetchNext24Hrs();
  },
  methods: {
    onNewAppointment() {
      this.$router.push('/appointments/create');
    },
    fetchNext24Hrs() {
      appointmentsService.fetchNext24HoursAppointments().then((response) => {
        this.upcoming = response.data.results;
      });
    },
    onViewAppointment(appointment) {
      this.$router.push(`/appointments/${appointment.pk}/view`);
    },
    trackSearch() {
      if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
        this.$mixpanel.track('search_appointment', {
          account_id: this.$store.state.account.user.user.email,
          location: '/appointments',
        });
      }
    },
    preDeleteProcess(item) {
      const isRecommendation = item.appointment_type === APPOINTMENT_TYPES.RECOMMENDATION;
      let message = `Are you sure you want to cancel and delete this appointment? We will remove it from your Appointments page${isRecommendation ? '.' : ' and send a cancellation email to your invitees.'}`;
      let title = 'Cancel Appointment';
      if (this.currentFilter.queryparams.past) {
        message = 'Are you sure you want to delete this past appointment from your history?';
        title = 'Delete Appointment';
      }

      EventBus.$emit('confirm', {
        title,
        message,
        positiveText: 'Delete Event',
        negativeText: 'Cancel',
        callback: (confirm) => {
          if (confirm) {
            this.onDeleteAppointment(item);
          }
        },
      });
    },
    cancelDeleteProcess() {
      this.deletingItem = null;
      this.deleteMessage = '';
      this.showDeleteDialog = false;
    },
    onPageChange(page) {
      this.truePage = page;
      this.$nextTick().then(() => window.scrollTo(0, 0));
    },
    onReload() {
      this.reload();
    },
    onDeleteAppointment(deletingItem) {
      this.$store.dispatch('appointments/deleteItem', deletingItem.pk).then(() => {
        // eslint-disable-next-line no-restricted-globals
        this.fetchData();
      }).catch(() => {
        EventBus.$emit('toast-error', 'There\'s been an error while cancelling this appointment, please try again in a few seconds.');
      }).finally(() => {
        this.showDeleteDialog = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.filteritem {
  cursor: pointer;
  font-size: 14px;
  padding: 3px 12px;
  border-radius: 2px;
  font-family: $font-family-sans-serif-light;
  transition: background-color .3s ease;

  &:hover {
    background-color: #F2F1EE;
  }

  &.selected {
    background-color: #F2F1EE;
    font-family: $font-family-sans-serif-semibold;
  }
}
.no-appointments {
  height: 50vh;
  width: 100%
}

.skeleton {
  border: 1px #D4D4D4 solid;
  border-radius: 5px;
  padding: 0.625rem;
}
.thumb {
  height: 133px;
  width: 257px;
}
</style>
